// src/pages/B2B/crm/api/crmClient.ts
import { useState, useEffect, useCallback } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import {
  Venue, VenueContact, VenueDetailResponse, VenueListFilters,
  VenueListResponse, ContactKind,
} from './types';

const EMPTY_LIST: VenueListResponse = { count: 0, venues: [] };

async function authedFetch(
  instance: any,
  accounts: any[],
  apiUrl: string,
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const tok = { accessToken: await acquireApiToken(instance, accounts[0]) };
  return fetch(`${apiUrl}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${tok.accessToken}`,
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
}

// ---- LIST ----
export function useVenueList(apiUrl: string, filters: VenueListFilters = {}) {
  const isAuthed = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [data, setData] = useState<VenueListResponse>(EMPTY_LIST);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const refetch = useCallback(() => setTrigger(t => t + 1), []);

  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (!isAuthed || accounts.length === 0) return;
    let mounted = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const qs = new URLSearchParams();
        if (filters.stage) qs.set('stage', filters.stage);
        if (filters.flag_for_ray) qs.set('flag_for_ray', 'true');
        if (filters.search) qs.set('search', filters.search);
        if (filters.sort) qs.set('sort', filters.sort);
        const path = `/admin/b2b/crm/venues${qs.toString() ? `?${qs}` : ''}`;
        const res = await authedFetch(instance, accounts, apiUrl, path);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        if (mounted) setData(json);
      } catch (e: any) {
        if (mounted) { setError(e?.message ?? 'Failed to load venues'); setData(EMPTY_LIST); }
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed, instance, accounts, apiUrl, filtersKey, trigger]);

  return { data, loading, error, refetch };
}

// ---- GET ONE ----
export function useVenue(apiUrl: string, id: string | undefined) {
  const isAuthed = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [data, setData] = useState<VenueDetailResponse | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(null);
  const [trigger, setTrigger] = useState(0);
  const refetch = useCallback(() => setTrigger(t => t + 1), []);

  useEffect(() => {
    if (!id || !isAuthed || accounts.length === 0) { setLoading(false); return; }
    let mounted = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await authedFetch(instance, accounts, apiUrl, `/admin/b2b/crm/venues/${id}`);
        const json = await res.json();
        if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`);
        if (mounted) setData(json);
      } catch (e: any) {
        if (mounted) { setError(e?.message ?? 'Failed to load venue'); setData(null); }
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [id, isAuthed, instance, accounts, apiUrl, trigger]);

  return { data, loading, error, refetch };
}

// ---- MUTATIONS ----
export function useCrmMutations(apiUrl: string) {
  const { instance, accounts } = useMsal();

  async function createVenue(payload: Partial<Venue> & { name: string; contacts?: Partial<VenueContact>[] }) {
    const res = await authedFetch(instance, accounts, apiUrl, `/admin/b2b/crm/venues`, {
      method: 'POST', body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json() as Promise<{ id: string }>;
  }

  async function updateVenue(id: string, payload: Partial<Venue>) {
    const res = await authedFetch(instance, accounts, apiUrl, `/admin/b2b/crm/venues/${id}`, {
      method: 'PATCH', body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json();
  }

  async function deleteVenue(id: string) {
    const res = await authedFetch(instance, accounts, apiUrl, `/admin/b2b/crm/venues/${id}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json();
  }

  async function createContact(venueId: string, c: { name: string; kind: ContactKind } & Partial<VenueContact>) {
    const res = await authedFetch(instance, accounts, apiUrl, `/admin/b2b/crm/venues/${venueId}/contacts`, {
      method: 'POST', body: JSON.stringify(c),
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json() as Promise<{ id: string }>;
  }

  async function updateContact(venueId: string, contactId: string, payload: Partial<VenueContact>) {
    const res = await authedFetch(instance, accounts, apiUrl,
      `/admin/b2b/crm/venues/${venueId}/contacts/${contactId}`, {
      method: 'PATCH', body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json();
  }

  async function deleteContact(venueId: string, contactId: string) {
    const res = await authedFetch(instance, accounts, apiUrl,
      `/admin/b2b/crm/venues/${venueId}/contacts/${contactId}`, { method: 'DELETE' });
    if (!res.ok) throw new Error((await res.json()).error || `HTTP ${res.status}`);
    return res.json();
  }

  return { createVenue, updateVenue, deleteVenue, createContact, updateContact, deleteContact };
}
